import { Select } from 'antd';
import _ from 'lodash';
import React, { FC, useContext, useEffect, useRef } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { AppContext } from '../../contexts/AppContext';
import { isBlockitAdmin, isAdminOrBlockitAdmin } from '../PrivateRoute';
import { Viewer, ViewerOrganization } from '../../../viewer';
import { useHistory } from 'react-router-dom';
import config from '../../../../config';
import { useLazyQuery } from '@apollo/client';
import { GetViewerOrganization } from '../../../../hooks/queries/useGetViewerOrganizationQuery';
import { updateSendsAndReceivesForAllLocationsLocation } from './Layout';
import { useBlockitThemeContext } from '../../../../themes';
import { textOnDarkMuted } from '../../../../themes/alluvium-colors';

const UserMenuStyles = styled.div<{ headerDarkBg?: string; textOnDark?: string; isAlluvium?: boolean }>`
  align-items: center;
  background-color: ${({ headerDarkBg, theme }) => headerDarkBg || theme.blueDarkest};
  box-sizing: border-box;
  color: ${({ textOnDark }) => textOnDark || '#fff'};
  display: flex;
  font-size: ${({ isAlluvium }) => (isAlluvium ? '14px' : '16px')};
  height: ${({ isAlluvium }) => (isAlluvium ? '44px' : '50px')};
  padding: 0 20px;

  .ant-select-selection {
    background-color: transparent;
    color: #fff;
  }

  && .ant-select-arrow,
  && .ant-select-arrow .anticon {
    background-color: transparent;
    color: #fff !important;
  }

  .ant-select-selection {
    margin-right: 2em;
    border-bottom: 0;
    font-size: ${({ isAlluvium }) => (isAlluvium ? '14px' : '16px')};
    color: #fff;
  }

  .ant-select {
    width: auto;
    min-width: 200px;
    max-width: 500px;
  }

  .ant-select-selection-selected-value {
    margin-right: 0.5em;
  }

  .currentOrg {
    padding-right: 42px;
  }

  .border-mimic {
    box-shadow: ${({ isAlluvium }) => (isAlluvium ? 'none' : 'inset 0 -2px 0 0 white')};
  }

  .nav {
    align-items: center;
    display: flex;
    flex: 1;
    justify-content: flex-end;

    > div {
      display: flex;
      justify-content: ${({ isAlluvium }) => (isAlluvium ? 'flex-end' : 'space-between')};

      > div > div:not(:last-child) {
        margin-right: 2rem;
      }

      > *:not(:last-child) {
        margin-right: 2rem;
      }
    }

    a,
    a:visited,
    .fakeLink {
      color: ${({ isAlluvium }) => (isAlluvium ? textOnDarkMuted : '#fff')};
      cursor: pointer;
      text-decoration: none;
    }

    ${({ isAlluvium }) =>
      isAlluvium &&
      `
      a:hover,
      a:focus,
      a.active {
        color: #fff;
      }
    `}
  }

  .alluvium-nav-muted {
    color: ${textOnDarkMuted};
  }

  .alluvium-nav-muted > a:hover,
  .alluvium-nav-muted .portal-item:hover {
    color: #fff;
  }

  .alluvium-active-portal,
  .alluvium-active-portal a {
    color: #fff;
  }
`;

function canSwitchLocations(currentOrganization: ViewerOrganization | null): boolean {
  return !!currentOrganization && currentOrganization.locations && currentOrganization.locations.length > 1;
}

function canSwitchOrganizations(viewer: Viewer | null): boolean {
  return !!viewer && viewer?.organizations && viewer.organizations.length > 1;
}

interface MenuSelectProps {
  ariaLabel: string;
  defaultValue: string;
  id: string;
  placeholder: string;
  setCurrent: (org: any) => void;
  options: ViewerOrganization[];
  key: string;
}

interface UserMenuProps {
  activeTab: string | null;
}

const MenuSelect: FC<MenuSelectProps> = ({
  ariaLabel,
  defaultValue,
  id,
  placeholder,
  setCurrent,
  options,
}): JSX.Element => {
  const history = useHistory();

  const handleSelect = (val: string) => {
    const organization = options.find(o => o.id === val);
    if (organization) {
      setCurrent(organization);
    }
    history.push('/');
  };

  return (
    <Select<string>
      id={id}
      showSearch
      defaultValue={defaultValue}
      dropdownMatchSelectWidth={false}
      placeholder={placeholder}
      optionFilterProp="children"
      onSelect={handleSelect}
      filterOption={(input, option) => {
        if (option.props.children && typeof option.props.children === 'string') {
          return option.props.children.toLowerCase().indexOf(input.toLowerCase()) >= 0;
        }
        return false;
      }}
      aria-label={ariaLabel}
    >
      {options.map(org => (
        <Select.Option key={org.id} value={org.id} style={org.isActive ? {} : { background: '#f7f7f7', color: '#ccc' }}>
          {`${org.name}`}
        </Select.Option>
      ))}
    </Select>
  );
};

export const sortViewerOrganizations = (orgs: ViewerOrganization[] | undefined): ViewerOrganization[] => {
  if (!orgs) {
    return [];
  }

  const activeOrgs = _.sortBy(
    orgs.filter(o => o.isActive),
    'name'
  );
  const inactiveOrgs = _.sortBy(
    orgs.filter(o => !o.isActive),
    'name'
  );

  return [...activeOrgs, ...inactiveOrgs];
};

type Portal = {
  id: 'admin' | 'referral';
  name: string;
};

const portals: Portal[] = [
  { id: 'admin', name: 'Admin' },
  { id: 'referral', name: 'Referral' },
];

function getActiveClasses(mode: string, portalId: string, isAlluvium?: boolean, onSettingsPage?: boolean): string {
  if (isAlluvium && onSettingsPage) {
    return '';
  }
  if (mode === portalId) {
    return isAlluvium ? 'font-bold alluvium-active-portal' : 'font-bold border-mimic';
  }
  return '';
}

export const UserMenu: FC<UserMenuProps> = ({ activeTab }): JSX.Element => {
  const {
    currentLocation,
    currentOrganization,
    mode,
    setCurrentLocation,
    setCurrentOrganization,
    switchMode,
    viewer,
    setViewer,
  } = useContext(AppContext);
  const { branding, isAlluvium } = useBlockitThemeContext();
  const [lazyGetViewerOrganization] = useLazyQuery(GetViewerOrganization);
  const previousOrgIdRef = useRef<string | null>(null);

  useEffect(() => {
    const fetchCurrentOrganization = async () => {
      if (currentOrganization) {
        const { data } = await lazyGetViewerOrganization({ variables: { organizationId: currentOrganization.id } });
        if (data && data.viewerOrganization && viewer) {
          const updatedOrgs = viewer.organizations.map(org =>
            org.id === data.viewerOrganization.id ? data.viewerOrganization : org
          );
          const updatedViewer = {
            ...viewer,
            organizations: updatedOrgs,
          };
          setViewer(updateSendsAndReceivesForAllLocationsLocation({ viewer: updatedViewer }));
        }
      }
    };

    if (currentOrganization && previousOrgIdRef.current !== currentOrganization.id) {
      fetchCurrentOrganization();
      previousOrgIdRef.current = currentOrganization.id;
    }

    if (activeTab === 'admin-portal') {
      switchMode('admin');
    }
  }, [activeTab, currentOrganization, lazyGetViewerOrganization, viewer, setViewer, switchMode]);

  const history = useHistory();
  const location = useLocation();
  const onSettingsPage = location.pathname.endsWith('/settings');

  useEffect(() => {
    if (mode === 'admin' && !isAdminOrBlockitAdmin(currentOrganization)) {
      switchMode('referral');
    }
  }, [currentOrganization, mode, switchMode]);

  useEffect(() => {
    if (mode === 'admin' && isAdminOrBlockitAdmin(currentOrganization)) {
      switchMode('admin');
    }
  }, [currentOrganization, mode, switchMode]);

  return (
    <UserMenuStyles
      headerDarkBg={isAlluvium ? branding.headerDarkBg : undefined}
      textOnDark={isAlluvium ? '#EDF2EB' : undefined}
      isAlluvium={isAlluvium}
    >
      {canSwitchOrganizations(viewer) ? (
        <MenuSelect
          id="organization-select"
          key={currentOrganization?.id || ''}
          defaultValue={currentOrganization?.id || ''}
          placeholder="Search for Organization"
          options={sortViewerOrganizations(viewer?.organizations)}
          setCurrent={setCurrentOrganization}
          ariaLabel="Organization Selector"
        />
      ) : (
        <span className="currentOrg">{currentOrganization && currentOrganization.name}</span>
      )}
      {canSwitchLocations(currentOrganization) ? (
        <Select<string>
          showSearch
          value={currentLocation?.id}
          dropdownMatchSelectWidth={false}
          placeholder="Search for Location"
          optionFilterProp="children"
          onSelect={(val: string) =>
            setCurrentLocation(currentOrganization?.locations.filter(l => l.id === val)[0] || null)
          }
          // onSearch={_.debounce(searchLocations, 500)}
          filterOption={(input, option) => {
            if (option.props.children && typeof option.props.children === 'string') {
              return option.props.children.toLowerCase().indexOf(input.toLowerCase()) >= 0;
            }
            return false;
          }}
          aria-label="Location Selector"
        >
          {currentOrganization?.locations.map(l => (
            <Select.Option value={l.id} key={l.id}>
              {`${l.name}`}
            </Select.Option>
          ))}
        </Select>
      ) : (
        <span>{currentLocation && currentLocation.name}</span>
      )}
      {viewer && (
        <div className="nav">
          <div
            className={`flex justify-end space-x-8 items-center ${
              isAlluvium ? 'alluvium-nav-muted' : 'text-white text-base'
            } whitespace-nowrap`}
          >
            {isBlockitAdmin(currentOrganization) && (
              <a href={`${config.endpoint}/admin`} target="_blank" rel="noopener noreferrer">
                Superadmin
              </a>
            )}
            {isAdminOrBlockitAdmin(currentOrganization) && (
              <div className="flex">
                {portals
                  .filter(portal => {
                    if (viewer?.profileLocationOnly) {
                      return portal.id === 'admin';
                    }
                    return true;
                  })
                  .map(portal => {
                    const classes = `cursor-pointer select-none py-3 portal-item ${getActiveClasses(
                      mode,
                      portal.id,
                      isAlluvium,
                      onSettingsPage
                    )}`;
                    return (
                      <div
                        className={classes}
                        onClick={() => {
                          switchMode(portal.id);
                          history.push(`/`);
                        }}
                        key={portal.id}
                      >
                        {portal.name} Portal
                      </div>
                    );
                  })}
              </div>
            )}
            <NavLink to={`/organizations/${currentOrganization?.id}/settings`}>Settings</NavLink>
            <Link to="/logout">Sign Out</Link>
          </div>
        </div>
      )}
    </UserMenuStyles>
  );
};
